/* ═══════════════════════════════════════════════════════════════════════════
   HOW THE MONEY IS AUTHORISED, AND HOW THE CUSTOMER IS TOLD

   Two separate questions that a booking screen keeps trying to merge, and
   they have to stay apart because the answers come from different places.

   ── One: how does the hold get authorised? ────────────────────────────────

   Vipps has a push. The customer's telephone rings, they approve the amount
   in an app they already trust, and the browser they started in never has to
   see a card number. It is the better road and it is the default wherever the
   number is a Norwegian mobile, because that is who Vipps can reach.

   A card has no push. There is nothing to ring. So the customer is sent a
   payment link, they open Stripe's own hosted page, and the authorisation
   happens there — on Stripe's page, under Stripe's certificate, with the card
   number never passing through Avtale.net or through this business. That is
   not a lesser road chosen for convenience: a booking form that collected a
   card number itself would put this platform inside PCI scope for no reason
   at all, and would be asking a customer to type sixteen digits into a page
   they have never seen before.

   **Stripe Checkout opens there and then.** Not a link sent somewhere for the
   customer to find later — the session opens while they are still looking at
   the screen they booked on, they authorise, and they are done. That removes
   a whole class of problem rather than solving it: nothing has to be
   delivered, so nothing can fail to arrive, and the customer never has to
   leave one device to finish on another.

   It also means the card road needs no contact detail of its own. An earlier
   version of this file sent the link by email and therefore demanded an
   address; with the session opening directly there is nothing to send, and
   the email goes back to being what it always was — optional, and only for
   the confirmation afterwards.

   What the session does carry is a deadline. A Checkout session that is
   opened and abandoned must not hold a slot in somebody's diary for ever, so
   the time is pencilled in until the session completes or runs out.

   The link is not decoration either. It carries a deadline, because an
   authorisation that never happens must not hold a slot in somebody's diary
   for ever — the appointment is pencilled in and released if nobody pays.

   ── Two: how is the confirmation delivered? ───────────────────────────────

   By SMS if there is a mobile number, by email if there is an email address,
   and by both when there are both. A customer who gave an email and got
   nothing there will say the booking was never confirmed, and they will be
   right to.

   The confirmation is sent when the booking exists. Not when the link is
   sent — "your appointment is confirmed" before anybody has paid is a lie
   that produces a no-show and an argument.

   ── What is not here ──────────────────────────────────────────────────────

   No keys. No merchant identifiers, no Stripe secret, no Vipps client
   secret — not as placeholders and not as empty strings that look like
   configuration somebody forgot to fill in. This module decides the route and
   writes the words; the transport belongs to the backend, and inventing a
   key here would be inventing a merchant relationship that does not exist.
   ═══════════════════════════════════════════════════════════════════════ */

import { valid, normalise } from './phone.js';

/** How long a Checkout session lasts. Long enough to find your card and
    short enough that a diary is not held hostage by somebody who changed
    their mind and closed the tab. */
export const LINK_MINUTES = 30;

export const METHODS = {
  vipps: {
    id: 'vipps',
    label: 'Vipps',
    mark: 'V',
    how: 'push',
    title: 'Approve in Vipps',
    lead: 'Your telephone rings. Approve the amount there and you are booked.',
    /* Vipps reaches Norwegian mobile numbers and nothing else. Offering it to
       somebody with a foreign number is offering them a push that will never
       arrive. */
    needs: 'a Norwegian mobile number',
  },
  card: {
    id: 'card',
    label: 'Card',
    mark: '▭',
    how: 'link',
    title: 'Pay by card',
    lead: 'Stripe’s own payment page opens here. Your card details never '
        + 'touch Avtale.net or this business.',
    needs: 'a card',
  },
};

/**
 * Both roads, always, with whether each one actually works for this number.
 *
 * The first version of this HID Vipps whenever the number was not a Norwegian
 * mobile, and that was wrong in two ways at once.
 *
 * The small way: it ran on an empty field, so a customer who had not yet
 * typed anything was shown a card-only screen and a sentence telling them the
 * number they had given was no good. They had not given one.
 *
 * The large way: hiding a payment method teaches the customer nothing. They
 * see one button, no explanation they can act on, and no idea that the app
 * they actually use is sitting one corrected digit away. Showing Vipps and
 * saying WHY it cannot be used turns a dead end into an instruction.
 *
 * So both are always on the screen. `ok` says whether it can be picked and
 * `why` is the sentence to put on it when it cannot.
 *
 * The check is `valid()` from domain/phone.js and not a regex of its own —
 * this file had one that accepted 2 through 9, which let every Norwegian
 * landline through to a Vipps push that would never arrive.
 */
export function waysFor(phone) {
  const typed = normalise(phone);

  /* Nothing typed is not a reason to rule anything out. It is the state
     every booking starts in. */
  if (!typed) return [
    { id: 'vipps', ok: true, why: null },
    { id: 'card', ok: true, why: null },
  ];

  const ok = valid(typed);
  return [
    {
      id: 'vipps',
      ok,
      /* "Still typing" and "that is not a Norwegian mobile" are different
         messages and the first version could not tell them apart: a UK
         number normalises to 447700900123, which happens to start with a 4,
         and was told it needed the full eight digits. Length has to come
         first. */
      why: ok ? null
        : (typed.length < 8 && /^[49]/.test(typed))
          ? 'Vipps needs the full eight digits.'
          : 'Vipps needs a Norwegian mobile number — this one cannot be sent a push.',
    },
    { id: 'card', ok: true, why: null },
  ];
}

/** Just the ids that work, for picking a default. */
export const methodsFor = phone => waysFor(phone).filter(w => w.ok).map(w => w.id);

/** Does this method authorise by ringing the customer, or by sending them
    somewhere? The whole of the booking screen's second step turns on it. */
export const isPush = method => METHODS[method]?.how === 'push';

/**
 * Does this method open a page the customer authorises on right now?
 *
 * The card road does. It is the counterpart of `isPush`: one rings the
 * customer's telephone, the other opens a page in front of them, and between
 * them they cover both roads with no third case where something has to be
 * delivered and waited for.
 */
export const opensCheckout = method => METHODS[method]?.how === 'link';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * What is wrong with the address, or null.
 *
 * An address is never required — Checkout opens directly and the confirmation
 * goes by SMS — but one that has been typed and is broken is worth catching,
 * because a confirmation silently going nowhere is how a customer concludes
 * the booking was never made.
 */
export function emailError(email) {
  const t = String(email || '').trim();
  return t && !EMAIL.test(t) ? 'That does not look like an email address.' : null;
}

/**
 * The Checkout session's own address.
 *
 * It is a route inside this app in the demo — there is no Stripe account to
 * open a session on — and the shape is the shape a real Checkout Session URL
 * has, so that the day there is one, the screen showing it does not change.
 */
export function payLink(reference) {
  return `#/pay/${encodeURIComponent(reference)}`;
}

/** When a payment link stops working. */
export function linkExpiry(now = new Date(), minutes = LINK_MINUTES) {
  return new Date(now.getTime() + minutes * 60000);
}

/**
 * Which ways the confirmation goes out.
 *
 * @param {object} to  {phone, email}
 * @returns {string[]} 'sms' and/or 'email', in the order they are sent
 */
export function channels(to) {
  const out = [];
  if (to?.phone) out.push('sms');
  if (to?.email) out.push('email');
  return out;
}

/** "by SMS and email" — the phrase a screen puts after "Confirmation sent". */
export function channelWords(to) {
  const c = channels(to);
  if (!c.length) return 'nowhere — we have no way to reach you';
  const words = c.map(x => (x === 'sms' ? 'SMS' : 'email'));
  return 'by ' + (words.length === 1 ? words[0] : words.join(' and '));
}

/**
 * The confirmation itself, in the words the customer receives.
 *
 * One function for both channels, because the two must not drift: an SMS that
 * says one deadline and an email that says another is worse than either on
 * its own. The email carries the same sentences with room for a subject and
 * the terms spelled out; the SMS is the same facts inside 320 characters.
 */
export function confirmation({ business, service, when, person, amount, free }) {
  const withWhom = person ? ` with ${person}` : '';
  const held = amount
    ? ` ${Number(amount).toLocaleString('nb-NO')} kr is held, not charged.`
    : '';
  const cancel = free ? ` Free to cancel ${free}.` : '';

  return {
    subject: `${business}: ${service}, ${when}`,
    sms: `${business}: ${service}${withWhom}, ${when}.${held}${cancel}`,
    lines: [
      `${service}${withWhom}`,
      when,
      business,
    ],
    body:
      `Your appointment is confirmed.\n\n` +
      `${service}${withWhom}\n${when}\n${business}\n\n` +
      (amount
        ? `${Number(amount).toLocaleString('nb-NO')} kr is held against your `
          + `payment method. It is not charged until the appointment is `
          + `carried out.\n\n`
        : '') +
      (free ? `You can cancel free of charge ${free}.\n\n` : '') +
      `This confirmation, the receipt afterwards and every message about this `
      + `appointment are also on your page at Avtale.net.`,
  };
}
